/**
 * Client legal documents (OPS Documents Phase 1).
 *
 * Rendered by the Documents module's Legal area. Upload (clients.edit) →
 * AI extraction draft → review gate (documents.confirm) writes vetted
 * values into the client record.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CLIENT_DOCUMENT_TYPE_LABELS,
  CLIENT_DOCUMENT_STATUS_LABELS,
  clientDocumentTypeSchema,
  JURISDICTIONS,
  type ClientDocumentOutput,
  type ClientDocumentType,
  type ClientOutput,
  type ConfirmClientDocumentInput,
  type ConfirmClientDocumentOutput,
  type ConfirmableField,
} from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { useEscape } from '../hooks/useEscape.js';
import { fmtDate, fmtDateTime, daysUntil } from '../lib/date.js';
import { ExpiryBadge } from './ExpiryBadge.js';
import { TaxCertificateDates, taxReviewValues, taxReviewPayload } from './TaxCertificateDates.js';
import { DateInput, validateDateInputs } from './DateInput.js';

const MAX_FILE_BYTES = 20 * 1024 * 1024;

function authHeaders(): HeadersInit {
  return { Authorization: `Bearer ${getAccessToken() ?? ''}` };
}

function StatusPill({ status }: { status: ClientDocumentOutput['status'] }) {
  const colour =
    status === 'CONFIRMED' ? 'pill-success'
    : status === 'PENDING_REVIEW' ? 'pill-warn'
    : status === 'EXTRACTION_FAILED' ? 'pill-danger'
    : 'pill-neutral';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${colour}`}>
      {CLIENT_DOCUMENT_STATUS_LABELS[status] ?? status}
    </span>
  );
}

/** Map the extraction draft onto the confirmable client fields (prefill). */
function prefillFields(doc: ClientDocumentOutput): Record<ConfirmableField, string> {
  const x = doc.extracted ?? {};
  const blank: Record<ConfirmableField, string> = {
    name: '', legalName: '', tradeLicenseNo: '', trn: '', incorporationDate: '', jurisdiction: '', fyEnd: '',
  };
  if (doc.type === 'TRADE_LICENSE') {
    blank.name = x.managerName ?? '';
    blank.legalName = x.legalName ?? '';
    blank.tradeLicenseNo = x.licenseNumber ?? '';
    // UAE business rule: the license ISSUE date doubles as the incorporation
    // date when no formation date is printed (fallback here, not in the model).
    blank.incorporationDate = x.establishmentDate ?? x.issueDate ?? '';
  } else if (doc.type === 'VAT_CERTIFICATE') {
    blank.legalName = x.legalName ?? '';
    blank.trn = x.trn ?? '';
  } else if (doc.type === 'CORPORATE_TAX_CERTIFICATE') {
    blank.legalName = x.legalName ?? '';
    blank.trn = x.trn ?? '';
    blank.fyEnd = x.firstPeriodEndDate ?? '';
  } else if (doc.type === 'CERTIFICATE_OF_INCORPORATION') {
    blank.legalName = x.legalName ?? '';
    blank.incorporationDate = x.incorporationDate ?? '';
  }
  // Identity documents (passport / Emirates ID / establishment card) carry no
  // client-record mappings — the review gate confirms the document dates only.
  if (doc.type !== 'PASSPORT' && doc.type !== 'EMIRATES_ID' && doc.type !== 'ESTABLISHMENT_CARD') {
    blank.jurisdiction = 'UAE';
  }
  return blank;
}

export function ClientDocumentsSection({ client }: { client: ClientOutput }) {
  const queryClient = useQueryClient();
  const { canDo } = useCapabilities();
  const [showUpload, setShowUpload] = useState(false);
  const [reviewDoc, setReviewDoc] = useState<ClientDocumentOutput | null>(null);
  const [datesDoc, setDatesDoc] = useState<ClientDocumentOutput | null>(null);

  const docs = useQuery({
    queryKey: ['client-legal-documents', client.id],
    queryFn: async () => {
      const res = await fetch(`/v1/clients/${client.id}/legal-documents`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: ClientDocumentOutput[] } };
      return body.data.items;
    },
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['client-legal-documents', client.id] });
    queryClient.invalidateQueries({ queryKey: ['client', client.id] });
  }

  async function downloadDoc(doc: ClientDocumentOutput) {
    const res = await fetch(`/v1/legal-documents/${doc.id}/download`, { headers: authHeaders() });
    if (!res.ok) return;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.originalName;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="bg-bg-panel border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-medium text-white">Legal documents</h2>
        {canDo('clients.edit') && (
          <button
            onClick={() => setShowUpload(true)}
            className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
          >
            + Upload document
          </button>
        )}
      </div>
      <p className="text-xs text-muted mb-4">
        AI extraction is a draft — a reviewer confirms before anything is written to the client record.
      </p>

      {docs.isLoading && <div className="text-muted p-4 text-center">Loading…</div>}
      {docs.error && <div className="text-rose-400 p-4 text-center">{(docs.error as Error).message}</div>}
      {docs.data && docs.data.length === 0 && (
        <div className="text-muted p-4 text-center">No legal documents yet.</div>
      )}
      {docs.data && docs.data.length > 0 && (
        <div className="border border-border rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
                {['Document', 'Type', 'Status', 'Issue', 'Expiry', 'Uploaded', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docs.data.map((d) => (
                <tr key={d.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                  <td className="px-4 py-3 text-white">{d.originalName}</td>
                  <td className="px-4 py-3 text-white">{CLIENT_DOCUMENT_TYPE_LABELS[d.type] ?? d.type}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={d.status} />
                    {d.status === 'EXTRACTION_FAILED' && d.extractionError && (
                      <p className="text-xs text-rose-400/80 mt-1 max-w-[220px]">{d.extractionError}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white">{fmtDate(d.issueDate)}</td>
                  <td className="px-4 py-3 text-white">
                    {d.expiryDate ? <ExpiryBadge expiryDate={d.expiryDate} /> : <span className="text-muted">—</span>}
                  </td>
                  <td className="px-4 py-3 text-white">
                    <span>{d.uploadedByName ?? '—'}</span>
                    <span className="block text-xs text-muted">{fmtDateTime(d.createdAt)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => downloadDoc(d)}
                        className="text-xs text-accent hover:text-white border border-border hover:border-accent px-2 py-1 rounded"
                      >
                        Download
                      </button>
                      {(d.status === 'PENDING_REVIEW' || d.status === 'EXTRACTION_FAILED') && canDo('documents.confirm') && (
                        <button
                          onClick={() => setReviewDoc(d)}
                          className="text-xs text-accent hover:text-white border border-border hover:border-accent px-2 py-1 rounded"
                        >
                          Review
                        </button>
                      )}
                      {d.status === 'CONFIRMED' && (
                        <span className="text-xs text-muted" title={d.confirmedAt ? fmtDateTime(d.confirmedAt) : ''}>
                          ✓ {d.confirmedByName ?? 'confirmed'}
                        </span>
                      )}
                      {d.status === 'CONFIRMED' && canDo('documents.confirm') && (
                        <button
                          onClick={() => setDatesDoc(d)}
                          className="text-xs text-accent hover:text-white border border-border hover:border-accent px-2 py-1 rounded"
                        >
                          Edit dates
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <UploadModal
          clientId={client.id}
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); refresh(); }}
        />
      )}
      {reviewDoc && (
        <ReviewModal
          doc={reviewDoc}
          client={client}
          onClose={() => setReviewDoc(null)}
          onDone={() => { setReviewDoc(null); refresh(); }}
        />
      )}
      {datesDoc && (
        <DatesModal
          doc={datesDoc}
          onClose={() => setDatesDoc(null)}
          onDone={() => { setDatesDoc(null); refresh(); }}
        />
      )}
    </section>
  );
}

/** Documents Phase 3: manual issue/expiry edit on a CONFIRMED document.
 *  The compliance renewal item follows the new expiry automatically. */
function DatesModal({ doc, onClose, onDone }: { doc: ClientDocumentOutput; onClose: () => void; onDone: () => void }) {
  useEscape(onClose);
  const [issueDate, setIssueDate] = useState(doc.issueDate?.slice(0, 10) ?? '');
  const [expiryDate, setExpiryDate] = useState(doc.expiryDate?.slice(0, 10) ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`/v1/legal-documents/${doc.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ issueDate: issueDate || null, expiryDate: expiryDate || null }),
      });
      const body = await res.json() as { ok?: boolean; error?: { message?: string } };
      if (!res.ok || !body.ok) {
        setError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
        return;
      }
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-sm mx-4 shadow-2xl animate-pop p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">Document dates</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        <p className="text-xs text-muted mb-3 truncate">{doc.originalName}</p>
        <form onSubmit={handleSave} className="space-y-3">
          <div>
            <label className="block text-xs text-muted mb-1">Issue date</label>
            <DateInput
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Expiry date</label>
            <DateInput
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
            />
            <p className="text-xs text-muted mt-1">
              Setting an expiry creates/moves the compliance renewal item (deadline = expiry, no grace period). Clearing it removes the open renewal item.
            </p>
          </div>
          {error && <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              {submitting ? 'Saving…' : 'Save dates'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UploadModal({ clientId, onClose, onDone }: { clientId: string; onClose: () => void; onDone: () => void }) {
  useEscape(onClose);
  const [type, setType] = useState<ClientDocumentType>('TRADE_LICENSE');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { setError('Choose a file first.'); return; }
    if (file.size > MAX_FILE_BYTES) { setError('File too large — max 20 MB.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('type', type);
      fd.append('file', file);
      const res = await fetch(`/v1/clients/${clientId}/legal-documents`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });
      const body = await res.json() as { ok?: boolean; error?: { message?: string } };
      if (!res.ok || !body.ok) {
        setError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
        return;
      }
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md mx-4 shadow-2xl animate-pop p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">Upload legal document</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-muted mb-1">Document type <span className="text-rose-400">*</span></label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ClientDocumentType)}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
            >
              {clientDocumentTypeSchema.options.map((t) => (
                <option key={t} value={t}>{CLIENT_DOCUMENT_TYPE_LABELS[t]}</option>
              ))}
            </select>
            <p className="text-xs text-muted mt-1">
              “Other” documents are stored without AI extraction.
            </p>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">File <span className="text-rose-400">*</span></label>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-muted file:mr-3 file:bg-bg-base file:border file:border-border file:rounded-lg file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:border-accent"
            />
            <p className="text-xs text-muted mt-1">PDF, JPG, PNG or WebP — max 20 MB.</p>
          </div>
          {error && <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button
              type="submit"
              disabled={submitting || !file}
              className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              {submitting ? (type === 'OTHER' ? 'Uploading…' : 'Uploading & extracting…') : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const FIELD_LABELS: Record<ConfirmableField, string> = {
  name: 'Contact name',
  legalName: 'Legal name',
  tradeLicenseNo: 'Trade license no.',
  trn: 'TRN',
  incorporationDate: 'Incorporation date',
  jurisdiction: 'Jurisdiction',
  fyEnd: 'Financial year end',
};

function ReviewModal({ doc, client, onClose, onDone }: {
  doc: ClientDocumentOutput;
  client: ClientOutput;
  onClose: () => void;
  onDone: () => void;
}) {
  useEscape(onClose);
  const [fields, setFields] = useState<Record<ConfirmableField, string>>(() => prefillFields(doc));
  // Documents Phase 3: reviewer-editable DOCUMENT dates (extraction may miss
  // them or the doc prints none) — written onto the document row at confirm.
  const [docIssue, setDocIssue] = useState(doc.issueDate?.slice(0, 10) ?? '');
  const [docExpiry, setDocExpiry] = useState(doc.expiryDate?.slice(0, 10) ?? '');
  const taxKind = doc.type === 'VAT_CERTIFICATE' ? 'VAT' : doc.type === 'CORPORATE_TAX_CERTIFICATE' ? 'CT' : null;
  const [taxDates, setTaxDates] = useState(() => taxReviewValues(doc.extracted));
  // Provenance flag for the UAE issue-date-as-incorporation-date fallback.
  const incorporationFromIssue = doc.type === 'TRADE_LICENSE'
    && !doc.extracted?.establishmentDate && !!doc.extracted?.issueDate;
  const [overwrites, setOverwrites] = useState<Set<ConfirmableField>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ConfirmClientDocumentOutput | null>(null);

  // Auth-gated preview — fetch as blob (same pattern as the branding logo).
  const preview = useQuery({
    queryKey: ['client-legal-document-file', doc.id],
    queryFn: async () => {
      const res = await fetch(`/v1/legal-documents/${doc.id}/file`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return URL.createObjectURL(await res.blob());
    },
    staleTime: Infinity,
  });
  useEffect(() => () => { if (preview.data) URL.revokeObjectURL(preview.data); }, [preview.data]);

  const currentValues: Record<ConfirmableField, string> = {
    name: client.name ?? '',
    legalName: client.legalName ?? '',
    tradeLicenseNo: client.tradeLicenseNo ?? '',
    trn: client.trn ?? '',
    incorporationDate: client.incorporationDate ? client.incorporationDate.slice(0, 10) : '',
    jurisdiction: client.jurisdiction ?? '',
    fyEnd: client.fyEnd ? client.fyEnd.slice(0, 10) : '',
  };

  function conflictsWithClient(field: ConfirmableField): boolean {
    const next = fields[field].trim();
    return next !== '' && currentValues[field] !== '' && currentValues[field] !== next;
  }

  function toggleOverwrite(field: ConfirmableField) {
    setOverwrites((s) => {
      const next = new Set(s);
      if (next.has(field)) next.delete(field); else next.add(field);
      return next;
    });
  }

  async function handleConfirm(event: React.MouseEvent<HTMLButtonElement>) {
    if (!validateDateInputs(event.currentTarget.closest('[data-date-form]') ?? document)) return;
    setError(null);
    setSubmitting(true);
    try {
      const input: ConfirmClientDocumentInput = {
        fields: {
          name: fields.name.trim() || null,
          legalName: fields.legalName.trim() || null,
          tradeLicenseNo: fields.tradeLicenseNo.trim() || null,
          trn: fields.trn.trim() || null,
          incorporationDate: fields.incorporationDate || null,
          jurisdiction: fields.jurisdiction || null,
          fyEnd: fields.fyEnd || null,
        },
        applyConflicts: [...overwrites].filter((f) => conflictsWithClient(f)),
        ...(taxKind ? { taxFields: taxReviewPayload(taxDates) } : { docDates: { issueDate: docIssue || null, expiryDate: docExpiry || null } }),
      };
      const res = await fetch(`/v1/legal-documents/${doc.id}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(input),
      });
      const body = await res.json() as { ok?: boolean; data?: ConfirmClientDocumentOutput; error?: { message?: string } };
      if (!res.ok || !body.ok || !body.data) {
        setError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
        return;
      }
      setResult(body.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const isImage = doc.mime.startsWith('image/');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-6xl max-h-[92vh] flex flex-col mx-4 shadow-2xl animate-pop">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-white">Review — {doc.originalName}</h2>
            <p className="text-xs text-muted mt-0.5">
              {CLIENT_DOCUMENT_TYPE_LABELS[doc.type]} · check the draft against the document, edit if needed, then confirm.
            </p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white text-sm">✕</button>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 px-5 py-4 overflow-y-auto">
          <div className="lg:w-1/2 min-h-[300px] bg-bg-base border border-border rounded-lg overflow-hidden flex items-center justify-center">
            {preview.isLoading && <span className="text-muted text-sm p-6">Loading preview…</span>}
            {preview.error && <span className="text-rose-400 text-sm p-6">{(preview.error as Error).message}</span>}
            {preview.data && isImage && (
              <img src={preview.data} alt={doc.originalName} className="max-h-[70vh] w-full object-contain" />
            )}
            {preview.data && !isImage && (
              <iframe src={preview.data} title={doc.originalName} className="w-full h-[70vh]" />
            )}
          </div>

          <div data-date-form className="lg:w-1/2 space-y-3">
            {doc.status === 'EXTRACTION_FAILED' && (
              <p className="text-xs text-rose-300 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">
                AI extraction failed{doc.extractionError ? `: ${doc.extractionError}` : ''}. Fill the fields manually against the document.
              </p>
            )}
            {taxKind ? <TaxCertificateDates kind={taxKind} values={taxDates} extracted={doc.extracted} onChange={(next) => {
              if (taxKind === 'CT' && (!fields.fyEnd || fields.fyEnd === taxDates.firstPeriodEndDate)) setFields((current) => ({ ...current, fyEnd: next.firstPeriodEndDate }));
              setTaxDates(next);
            }} /> : <div className="border border-border rounded-lg p-3 space-y-3">
              <p className="text-xs font-medium text-white">Document dates <span className="text-muted font-normal">— stored on the document itself; expiry drives the compliance renewal item</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted mb-1">Issue date</label>
                  <DateInput
                    value={docIssue}
                    onChange={(e) => setDocIssue(e.target.value)}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Expiry date</label>
                  <DateInput
                    value={docExpiry}
                    onChange={(e) => setDocExpiry(e.target.value)}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
                  />
                  {docExpiry && daysUntil(docExpiry) < 0 && (
                    <p className="text-xs text-rose-300 mt-1">
                      Expired {-daysUntil(docExpiry)} {-daysUntil(docExpiry) === 1 ? 'day' : 'days'} ago — the renewal item will be created already overdue.
                    </p>
                  )}
                </div>
              </div>
            </div>}

            {(Object.keys(FIELD_LABELS) as ConfirmableField[]).map((field) => (
              <div key={field}>
                <label className="block text-xs text-muted mb-1">{FIELD_LABELS[field]}</label>
                {field === 'jurisdiction' ? (
                  <select
                    value={fields.jurisdiction}
                    onChange={(e) => setFields((f) => ({ ...f, jurisdiction: e.target.value }))}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
                  >
                    <option value="">—</option>
                    {JURISDICTIONS.map((j) => (
                      <option key={j.code} value={j.code}>{j.label}</option>
                    ))}
                  </select>
                ) : field === 'incorporationDate' || field === 'fyEnd' ? (
                  <DateInput
                    value={fields[field]}
                    onChange={(e) => setFields((f) => ({ ...f, [field]: e.target.value }))}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
                  />
                ) : (
                  <input
                    value={fields[field]}
                    onChange={(e) => setFields((f) => ({ ...f, [field]: e.target.value }))}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
                  />
                )}
                {field === 'incorporationDate' && incorporationFromIssue && (
                  <p className="text-xs text-muted mt-1">
                    Taken from the license issue date — the license prints no separate formation date. Override if different.
                  </p>
                )}
                {conflictsWithClient(field) && (
                  <label className="mt-1.5 flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overwrites.has(field)}
                      onChange={() => toggleOverwrite(field)}
                      className="mt-0.5"
                    />
                    <span>
                      Client already has “{field === 'incorporationDate' || field === 'fyEnd' ? fmtDate(currentValues[field]) : currentValues[field]}”.
                      {overwrites.has(field) ? ' Will be overwritten.' : ' Tick to overwrite — otherwise the current value is kept.'}
                    </span>
                  </label>
                )}
              </div>
            ))}

            <p className="text-xs text-muted">
              Empty fields are not written. Fields without a conflict fill empty client values automatically.
            </p>

            {error && <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{error}</p>}

            {result ? (
              <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 space-y-1">
                <p>Document confirmed.</p>
                {result.written.length > 0 && <p>Written to client: {result.written.map((f) => FIELD_LABELS[f]).join(', ')}.</p>}
                {result.skippedConflicts.length > 0 && (
                  <p>Kept existing values for: {result.skippedConflicts.map((f) => FIELD_LABELS[f]).join(', ')}.</p>
                )}
                {result.written.length === 0 && result.skippedConflicts.length === 0 && <p>No client fields changed.</p>}
              </div>
            ) : (
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
                <button
                  onClick={handleConfirm}
                  disabled={submitting}
                  className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
                >
                  {submitting ? 'Confirming…' : 'Confirm & apply'}
                </button>
              </div>
            )}
            {result && (
              <div className="flex justify-end pt-1">
                <button
                  onClick={onDone}
                  className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
